import { Button, Divider, Modal, notification, Row, Select, Space } from "antd";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";

import {
  ExclamationCircleFilled,
  MinusCircleFilled,
  MinusCircleOutlined,
  PlusCircleTwoTone,
} from "@ant-design/icons";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { RootState } from "../../../../app/store";
import { setSelectedServiceTypeId } from "../../../../features/serviceType/serviceTypeListSlice";
import { updateUserRequestValue } from "../../../../features/userRequest/pendingUserRequestSlice";
import { removeProviderFromUserRequestAsync } from "../../../../features/userRequest/removeProviderFromUserRequestSlice";
import {
  setSelectedAdminId,
  setSelectedCountryId,
} from "../../../../features/userRequest/userRequestListSlice";
import {
  cancelUserRequest,
  completeUserRequest,
  createTripBundle,
  deleteUserRequest,
} from "../../../../services/userRequestApi";
import { TRIP_STATUS } from "../../../../shared/constants/TripStatus";
import { WebRoutes } from "../../../../shared/constants/WebRoutes";
import UserRequestDTO from "../../../../shared/dtos/userRequestDTO";
import { COUNTRIES } from "../../../../utils/constants/countries";
import {
  selectCurrentAdminType,
  selectSelectedCountryId,
} from "../../../../utils/helpers/selectorHelper";
import { OperatorTypeId } from "../../../../shared/constants/Resources";

interface FilterProps {
  setRefreshData: Dispatch<SetStateAction<boolean>>;
  selectedTrips: UserRequestDTO[];
}

export const Filter = ({ setRefreshData, selectedTrips }: FilterProps) => {
  const navigate: NavigateFunction = useNavigate();
  const dispatch = useAppDispatch();
  const cancellableStatus = [
    TRIP_STATUS.Searching,
    TRIP_STATUS.Accepted,
    TRIP_STATUS.Arrived,
    TRIP_STATUS.Started,
    TRIP_STATUS.Created,
    TRIP_STATUS.Scheduled,
  ];

  const deletableStatus = [TRIP_STATUS.Searching, TRIP_STATUS.Scheduled];

  const { value: serviceTypes, selectedServiceTypeId } = useAppSelector(
    (state: RootState) => state.serviceTypeList,
  );
  const adminsForSelect = useAppSelector(
    (state: RootState) => state.adminsForSelect.value,
  );
  const selectedAdminId = useAppSelector(
    (state: RootState) => state.userRequestList.selectedAdminId,
  );
  const { status } = useAppSelector(
    (state: RootState) => state.userRequestList,
  );
  const { status: removeStatus } = useAppSelector(
    (state: RootState) => state.removeProviderFromUserRequest,
  );

  const currentUserRequest =
    selectedTrips && selectedTrips.length === 1 ? selectedTrips[0] : undefined;

  const isCancellable = cancellableStatus.some(
    (c) => c === currentUserRequest?.status,
  );

  const isPackageTrips = selectedTrips.length >= 2;
  const isProviderMissingInAllTrips = selectedTrips.every(
    (x) => x.provider === undefined || x.provider === null,
  );
  const isEveryTripCancellable = selectedTrips.every((x) =>
    cancellableStatus.includes(x.status!),
  );

  const showCancelConfirm = async (id?: number | null) => {
    Modal.confirm({
      title: "Estas seguro de cancelar esta solicitud de viaje?",
      icon: <ExclamationCircleFilled />,
      okText: "Si",
      okType: "danger",
      cancelText: "No",
      async onOk() {
        await cancelUserRequest({ userRequestId: id }).finally(() =>
          Modal.destroyAll(),
        );
        setRefreshData(true);
      },
      onCancel() {
        Modal.destroyAll();
      },
    });
  };

  const showCompleteConfirm = async (id?: number | null) => {
    Modal.confirm({
      title: "Estas seguro de completar este viaje?",
      icon: <ExclamationCircleFilled />,
      okText: "Si",
      okType: "danger",
      cancelText: "No",
      async onOk() {
        await completeUserRequest({ userRequestId: id }).finally(() =>
          Modal.destroyAll(),
        );
        setRefreshData(true);
      },
      onCancel() {
        Modal.destroyAll();
      },
    });
  };

  const showDeleteConfirm = async (id?: number | null) => {
    Modal.confirm({
      title: "Estas seguro de eliminar esta solicitud de viaje?",
      icon: <ExclamationCircleFilled />,
      okText: "Si",
      okType: "danger",
      cancelText: "No",
      async onOk() {
        await deleteUserRequest({ userRequestId: id }).finally(() =>
          Modal.destroyAll(),
        );
        setRefreshData(true);
      },
      onCancel() {
        Modal.destroyAll();
      },
    });
  };

  const assignRequestsToDriver = () => {
    if (selectedTrips.length < 2) {
      notification.warning({
        message:
          "Debe seleccionar al menos dos viajes para crear un paquete de viajes",
        placement: "bottom",
        key: "global",
      });
      return;
    }

    dispatch(updateUserRequestValue(selectedTrips));
    navigate(WebRoutes.AssignProviderRoute, {
      state: { package: true },
      replace: true,
    });
  };

  const handleCreateTripBundle = async () => {
    if (selectedTrips.length < 2) {
      notification.warning({
        message:
          "Debe seleccionar al menos dos viajes para crear un paquete de viajes",
        placement: "bottom",
        key: "global",
      });
      return;
    }

    await createTripBundle(selectedTrips).then((x) => {
      notification.success({
        message: "Se ha creado exitosamente el paquete de viajes",
        placement: "bottom",
        key: "global",
      });
    });
  };

  const handleOnCountryChange = (id: number) => {
    dispatch(setSelectedCountryId(id));
    setRefreshData(true);
  };

  const handleOnAdminChange = (id: number) => {
    dispatch(setSelectedAdminId(id));
    setRefreshData(true);
  };

  const handleOnChange = (id: number) => {
    dispatch(setSelectedServiceTypeId(id));
  };

  return (
    <Row
      style={{
        marginBottom: "10px",
        overflowX: "scroll",
        paddingBottom: "10px",
      }}
    >
      <Space>
        <Select
          style={{ width: 210 }}
          onChange={handleOnChange}
          defaultValue={selectedServiceTypeId}
          placeholder="Selecciona un servicio"
          optionFilterProp="label"
          showSearch
          allowClear
          loading={status === "loading"}
          options={serviceTypes?.map((x) => ({ value: x.id, label: x.name }))}
        />
        <Select
          style={{ width: 210 }}
          onChange={handleOnCountryChange}
          defaultValue={selectSelectedCountryId()}
          placeholder="Seleccione un país"
          optionFilterProp="label"
          showSearch
          allowClear
          loading={status === "loading"}
          options={COUNTRIES}
        />
        <Select
          style={{ width: 210 }}
          onChange={handleOnAdminChange}
          defaultValue={selectedAdminId}
          showSearch
          optionFilterProp="label"
          placeholder="Seleccione una compañía"
          allowClear
          loading={status === "loading"}
          options={adminsForSelect?.map((x) => ({
            value: x.id,
            label: x.name,
          }))}
        />
        {currentUserRequest && !isCancellable && (
          <>
            <Divider type="vertical" />
            <Row style={{ width: "150px", textAlign: "center", color: "red" }}>
              No se puede remover conductor ahora
            </Row>
          </>
        )}
        {currentUserRequest &&
          !isProviderMissingInAllTrips &&
          isCancellable && (
            <>
              <Divider type="vertical" />
              <Button
                loading={removeStatus === "loading"}
                onClick={async () => {
                  if (currentUserRequest.id) {
                    await Promise.all(
                      selectedTrips.map(async (p) => {
                        return await dispatch(
                          removeProviderFromUserRequestAsync({
                            userRequestId: p.id,
                          }),
                        );
                      }),
                    );
                    notification.success({
                      message: "Se removio el conductor existosamente",
                      description: `Se removio el conductor del viaje ${currentUserRequest.bookingId}`,
                      placement: "bottom",
                      key: "global",
                    });
                    setRefreshData(true);
                  }
                }}
              >
                Remover conductor
              </Button>
            </>
          )}
        {!currentUserRequest &&
          isPackageTrips &&
          isProviderMissingInAllTrips &&
          isEveryTripCancellable && (
            <>
              <Divider type="vertical" />
              <Button onClick={assignRequestsToDriver}>
                Asignar conductor a multiples viajes
              </Button>
            </>
          )}

        {currentUserRequest &&
          isProviderMissingInAllTrips &&
          isEveryTripCancellable && (
            <>
              <Divider type="vertical" />
              {/* <Button
                onClick={() =>
                  navigate(
                    WebRoutes.TripDetailsEditRoute.replace(
                      ":tripId",
                      currentUserRequest.id?.toString()!,
                    ),
                    { replace: true },
                  )
                }
              >
                Editar viaje
              </Button> */}
              <Button
                onClick={() => {
                  dispatch(updateUserRequestValue(selectedTrips));
                  navigate(WebRoutes.AssignProviderRoute, { replace: true });
                }}
              >
                Asignar conductor
              </Button>
            </>
          )}
        {currentUserRequest &&
          deletableStatus.includes(currentUserRequest.status!) && (
            <>
              <Divider type="vertical" />
              <Button
                danger
                icon={<MinusCircleOutlined />}
                onClick={() => showCancelConfirm(currentUserRequest.id)}
              >
                Cancelar
              </Button>
              {selectCurrentAdminType() !== OperatorTypeId && (
                <Button
                  type="primary"
                  danger
                  icon={<MinusCircleFilled />}
                  onClick={() => showDeleteConfirm(currentUserRequest.id)}
                >
                  Eliminar
                </Button>
              )}
            </>
          )}
        {currentUserRequest && (
          <>
            {!deletableStatus.includes(currentUserRequest.status!) && (
              <Divider type="vertical" />
            )}
            <Button
              type="primary"
              color="green"
              variant="solid"
              onClick={() => showCompleteConfirm(currentUserRequest.id)}
            >
              Completar viaje
            </Button>
          </>
        )}
        <Divider type="vertical" />
        {!currentUserRequest &&
          isPackageTrips &&
          isProviderMissingInAllTrips &&
          isEveryTripCancellable && (
            <>
              <Button
                type="primary"
                onClick={handleCreateTripBundle}
                icon={<PlusCircleTwoTone />}
              >
                Paquete de viajes
              </Button>
            </>
          )}
        <Button
          type="primary"
          icon={<PlusCircleTwoTone />}
          onClick={() => navigate(WebRoutes.TripCreateRoute, { replace: true })}
        >
          Viaje
        </Button>
        <Button
          type="primary"
          onClick={() =>
            navigate(WebRoutes.DispatcherGroupTrip, {
              state: { dispatcher: true },
              replace: true,
            })
          }
          icon={<PlusCircleTwoTone />}
        >
          Viaje en grupo
        </Button>
      </Space>
    </Row>
  );
};

export default Filter;
